import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import humanTime from 'flarum/common/helpers/humanTime';
import extractText from 'flarum/common/utils/extractText';

import n8nMark from '../utils/n8nMark';
import meetupBody from '../utils/meetupBody';
import {
  deleteMeetupComment,
  fetchMeetup,
  formatMeetupDate,
  postMeetupComment,
  toggleMeetupLike,
} from '../utils/meetups';
import type { Meetup, MeetupComment, MeetupCommentAuthor, MeetupPhoto } from '../utils/meetups';

/**
 * `/encuentros/{slug}` en el SPA — la ficha de un encuentro PASADO (D6/D6b).
 *
 * LAS TRES PUERTAS DE ESTA PANTALLA, que son tres y no una (decisiones de producto, David
 * 2026-08-12):
 *
 *   VER      cualquiera, sin cuenta. Descripcion y fotos son publicas y esta pagina no pide
 *            registrarse para leerlas.
 *   ME GUSTA cualquier cuenta con sesion, de n8n o no. Sin sesion el contador se ve y el boton
 *            no hace nada — se pinta como estado, no como un control que engane (misma regla
 *            que D8 en `addInterestButton`).
 *   COMENTAR solo miembros de n8n Chile. Anonimo y Umbral-sin-n8n ven CUANTOS comentarios hay
 *            y la tarjeta-puerta; los cuerpos no llegan al navegador (D6 del mockup y leccion
 *            X1 del Spec §11).
 *
 * NINGUNA DE LAS TRES SE DECIDE AQUI. El servidor manda `likes.can`, `comments.canRead` y
 * `comments.canWrite`, y cuando no toca leer, `comments.items` llega VACIO — no lleno y oculto
 * con CSS. Este fichero solo elige que pintar con lo que recibio, asi que un fallo suyo no
 * puede filtrar nada que el servidor no hubiera mandado ya.
 */
export default class EncuentroPage extends Page {
  meetup: Meetup | null = null;
  failed = false;
  notFound = false;

  /** Borrador del composer. Vive en el componente: no hay nada que persistir entre visitas. */
  draft = '';
  posting = false;
  /** Ids con un borrado de admin en vuelo: deshabilitan SU boton, no todos. */
  removing: Record<string, boolean> = {};
  liking = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitleCount(0);
    this.load();
  }

  view() {
    if (this.notFound) {
      return (
        <div className="CmMeetup-container container">
          <div className="CmMeetup-empty" data-cm-meetup-missing="1">
            <b>{this.t('missing_title')}</b>
            <p>{this.t('missing_body')}</p>
            <Link className="Button" href={app.route('umbral-comunidad.encuentros')}>
              {this.t('back')}
            </Link>
          </div>
        </div>
      );
    }

    if (this.failed) {
      return (
        <div className="CmMeetup-container container">
          <p className="CmMeetups-error" data-cm-meetup-error="1">
            {this.t('failed')}
          </p>
        </div>
      );
    }

    if (!this.meetup) {
      return (
        <div className="CmMeetup-container container">
          <LoadingIndicator />
        </div>
      );
    }

    const meetup = this.meetup;

    return (
      <div className="CmMeetup-container container">
        <article className="CmMeetup" data-cm-meetup={meetup.slug}>
          <Link className="CmMeetup-back" href={app.route('umbral-comunidad.encuentros')}>
            {this.t('back')}
          </Link>

          {!meetup.published && (
            <p className="CmMeetup-draft" data-cm-meetup-draft="1">
              {this.t('draft_note')}
            </p>
          )}

          <header className="CmMeetup-head">
            <h1 className="CmMeetup-title">{meetup.title}</h1>
            <div className="CmMeetup-meta">
              <time>{formatMeetupDate(meetup.heldOn)}</time>
              {meetup.place ? [<span>·</span>, <span>{meetup.place}</span>] : null}
            </div>
          </header>

          {meetup.photos.length ? (
            <div className="CmMeetup-gallery" data-cm-meetup-photos={meetup.photos.length}>
              {meetup.photos.map((photo, index) => this.photo(photo, index))}
            </div>
          ) : null}

          <div className="CmMeetup-body">{this.body(meetup)}</div>

          <div className="CmMeetup-actions">{this.likeButton(meetup)}</div>

          {this.comments(meetup)}
        </article>
      </div>
    );
  }

  photo(photo: MeetupPhoto, index: number) {
    return (
      <figure className="CmMeetup-photo">
        <img
          src={photo.url}
          alt={photo.caption || ''}
          width={photo.width || undefined}
          height={photo.height || undefined}
          /* La primera es la portada (y la imagen de share): se carga con la pagina; el resto
             en diferido, que en una galeria de veinte es la diferencia entre abrir y esperar. */
          loading={index === 0 ? 'eager' : 'lazy'}
        />
        {photo.caption ? <figcaption>{photo.caption}</figcaption> : null}
      </figure>
    );
  }

  /**
   * El cuerpo de la ficha, que SI admite markdown desde PKG-FORO-ENCUENTROS-POLISH.
   *
   * No se analiza aqui: el servidor manda el resultado ya estructurado en `bodyBlocks` y
   * `meetupBody()` lo convierte en vnodos (ver la cabecera de `utils/meetupBody.tsx` para por
   * que la gramatica vive en un solo lado).
   *
   * EL RESPALDO NO ES DECORATIVO. Un payload servido antes de este paquete —o guardado en una
   * cache— no trae `bodyBlocks`, y sin esta rama la ficha se quedaria sin cuerpo. Se cae al
   * render de texto plano de siempre, que es peor que el markdown y muchisimo mejor que nada.
   */
  body(meetup: Meetup) {
    if (Array.isArray(meetup.bodyBlocks)) return meetupBody(meetup.bodyBlocks);

    return this.paragraphs(meetup.body);
  }

  /**
   * Texto plano a parrafos. Lo usan los COMENTARIOS, que siguen siendo texto plano a proposito
   * (ver `MeetupComment` y la migracion), y el respaldo de `body()` de aqui arriba.
   *
   * Mithril mete cada trozo como nodo de texto, asi que no hay forma de que lo escrito se
   * convierta en marcado.
   */
  paragraphs(text: string) {
    return String(text || '')
      .split(/\n{2,}/)
      .filter((block) => block.trim() !== '')
      .map((block) => (
        <p>
          {block.split('\n').map((line, index) => (index === 0 ? line : [<br />, line]))}
        </p>
      ));
  }

  likeButton(meetup: Meetup) {
    const { count, mine, can } = meetup.likes;

    return (
      <div className="CmMeetupLike" data-cm-like-count={count} data-cm-like-mine={mine ? '1' : '0'}>
        <Button
          className={'Button CmMeetupLike-btn' + (mine ? ' CmMeetupLike-btn--on' : '')}
          data-cm-like-btn="1"
          icon={mine ? 'fas fa-heart' : 'far fa-heart'}
          disabled={!can || this.liking}
          onclick={() => this.toggleLike(meetup)}
        >
          {app.translator.trans('umbral-comunidad.forum.encuentros.like.' + (mine ? 'on' : 'off'), { count })}
        </Button>
        {!can && <span className="CmMeetupLike-note">{this.t('like.guest_note')}</span>}
      </div>
    );
  }

  comments(meetup: Meetup) {
    const { count, canRead, canWrite, items } = meetup.comments;

    return (
      <section className="CmMeetup-comments" data-cm-comment-count={count} data-cm-can-comment={canWrite ? '1' : '0'}>
        <h2 className="CmMeetup-commentsTitle">
          {app.translator.trans('umbral-comunidad.forum.encuentros.comments.count', { count })}
          {/* El alcance se dice SIEMPRE, tambien a los miembros: es la etiqueta que explica por
              que esta conversacion no esta en el feed publico. */}
          <span className="CmMeetup-commentsScope">{this.t('comments.scope')}</span>
        </h2>

        {canRead ? (
          <div className="CmMeetup-commentList">
            {items.length ? items.map((comment) => this.comment(meetup, comment)) : <p className="CmMeetup-noComments">{this.t('comments.empty')}</p>}
          </div>
        ) : null}

        {canWrite ? this.composer(meetup) : this.gate()}
      </section>
    );
  }

  comment(meetup: Meetup, comment: MeetupComment) {
    const author = comment.author;
    const busy = !!this.removing[String(comment.id)];

    return (
      <div className="CmMeetupComment" data-cm-comment={comment.id}>
        <span className="CmMeetupComment-avatar">{this.avatar(author)}</span>
        <div className="CmMeetupComment-body">
          <span className="CmMeetupComment-who">
            {/* Cuenta borrada: la migracion conserva las palabras y suelta el nombre, igual que
                hace el core con los posts. No se inventa un autor. */}
            <b>{author ? author.displayName : '—'}</b>
            {comment.createdAt ? <span className="CmMeetupComment-when">{humanTime(new Date(comment.createdAt))}</span> : null}
          </span>
          <div className="CmMeetupComment-text">{this.paragraphs(comment.content)}</div>
        </div>
        {meetup.canManage && (
          <Button
            className="Button Button--link CmMeetupComment-remove"
            data-cm-comment-remove={comment.id}
            icon="fas fa-trash"
            loading={busy}
            disabled={busy}
            title={extractText(this.t('comments.remove'))}
            onclick={() => this.removeComment(meetup, comment)}
          />
        )}
      </div>
    );
  }

  /**
   * Avatar de un comentarista, a mano.
   *
   * NO se usa el ayudante `flarum/common/helpers/avatar`: espera un MODELO de usuario y llama a
   * `user.avatarUrl()` y `user.displayName()` como métodos. Lo que llega aquí es el objeto plano
   * del payload propio de esta pantalla (no viaja por `app.store`), así que ese ayudante
   * reventaría el render entero del comentario. Se emite el mismo marcado que el core —clase
   * `.Avatar`, imagen o inicial— para heredar su CSS.
   */
  avatar(author: MeetupCommentAuthor | null) {
    if (!author) return <span className="Avatar CmMeetupComment-avatarNone">—</span>;

    if (author.avatarUrl) {
      // `alt` vacío: el nombre va escrito al lado y repetirlo sólo alarga lo que se escucha.
      return <img className="Avatar" src={author.avatarUrl} alt="" />;
    }

    return <span className="Avatar">{(author.displayName || '?').charAt(0).toUpperCase()}</span>;
  }

  composer(meetup: Meetup) {
    return (
      <form
        className="CmMeetup-composer"
        data-cm-meetup-composer="1"
        onsubmit={(event: Event) => {
          event.preventDefault();
          this.submitComment(meetup);
        }}
      >
        <textarea
          className="FormControl CmMeetup-composerInput"
          rows="3"
          placeholder={extractText(this.t('comments.placeholder'))}
          value={this.draft}
          disabled={this.posting}
          oninput={(event: any) => {
            this.draft = event.target.value;
          }}
        />
        <Button
          type="submit"
          className="Button Button--primary CmMeetup-composerSubmit"
          loading={this.posting}
          disabled={this.posting || this.draft.trim() === ''}
        >
          {this.t('comments.submit')}
        </Button>
      </form>
    );
  }

  /**
   * La tarjeta-puerta de D6. Es lo UNICO que se le cuenta de la conversacion a quien no esta en
   * el grupo — junto al contador del titulo, que si se muestra: la senal social se ve, el
   * contenido no (Spec §11).
   */
  gate() {
    return (
      <div className="CmMeetup-gate" data-cm-meetup-gate="1">
        {n8nMark('cm-n8n-mark CmMeetup-gateMark')}
        <div className="CmMeetup-gateText">
          <b>{this.t('comments.gate_title')}</b>
          <p>{this.t('comments.gate_body')}</p>
        </div>
        {/* MISMA etiqueta con sesión y sin ella: el HTML server-side pinta esta tarjeta antes
            de que Mithril monte, y dos textos distintos para el mismo botón se verían cambiar
            solos al cargar. Lo que sí cambia es el destino — ver `gateHref`. */}
        <Link className="CmMeetup-gateCta" href={this.gateHref()}>
          {this.t('comments.gate_cta')}
        </Link>
      </div>
    );
  }

  /**
   * Sin sesion el boton lleva a D7 —la pagina publica del grupo, que explica de que va— y no al
   * Form B, que exige cuenta y devolveria a quien lo pulse a una pantalla que le dice que no
   * puede estar ahi. Es la misma regla del CTA de la puerta B en la home (Spec §10).
   */
  gateHref(): string {
    return app.session.user
      ? app.route('umbral-comunidad.solicitud')
      : app.route('umbral-comunidad.n8nChile');
  }

  // --- datos ------------------------------------------------------------------------------

  slug(): string {
    return String(m.route.param('slug') || '');
  }

  load() {
    this.failed = false;
    this.notFound = false;

    fetchMeetup(this.slug())
      .then((data) => {
        this.meetup = data.meetup;
        app.setTitle(data.meetup.title);
        m.redraw();
      })
      .catch((error: any) => {
        // 404 cubre «no existe» Y «es borrador» — el servidor no los distingue a proposito, y
        // esta pantalla tampoco puede: dice que no hay ficha ahi y ofrece la salida.
        if (error && error.status === 404) this.notFound = true;
        else this.failed = true;

        m.redraw();
      });
  }

  toggleLike(meetup: Meetup) {
    if (!meetup.likes.can || this.liking) return;

    const previous = { ...meetup.likes };
    const next = !meetup.likes.mine;

    // Optimista: el corazon responde al clic y no a la latencia. El servidor devuelve el
    // recuento real y se reconcilia debajo; si falla, se restaura lo que habia.
    meetup.likes = { ...meetup.likes, mine: next, count: Math.max(0, meetup.likes.count + (next ? 1 : -1)) };
    this.liking = true;
    m.redraw();

    toggleMeetupLike(meetup.slug, next)
      .then((data) => {
        meetup.likes = data.likes;
        this.liking = false;
        m.redraw();
      })
      .catch(() => {
        meetup.likes = previous;
        this.liking = false;
        m.redraw();
      });
  }

  submitComment(meetup: Meetup) {
    const content = this.draft.trim();

    if (content === '' || this.posting) return;

    this.posting = true;
    m.redraw();

    postMeetupComment(meetup.slug, content)
      .then((data) => {
        meetup.comments.items = meetup.comments.items.concat([data.comment]);
        meetup.comments.count = data.count;
        this.draft = '';
        this.posting = false;
        m.redraw();
      })
      .catch(() => {
        // El borrador NO se borra: quien acaba de escribir tres parrafos no debe perderlos
        // porque el envio fallara. Flarum ya saco su alerta con el motivo.
        this.posting = false;
        m.redraw();
      });
  }

  removeComment(meetup: Meetup, comment: MeetupComment) {
    const id = String(comment.id);

    if (!confirm(extractText(this.t('comments.remove_confirm')))) return;

    this.removing[id] = true;
    m.redraw();

    deleteMeetupComment(meetup.slug, comment.id)
      .then((data) => {
        delete this.removing[id];
        meetup.comments.items = meetup.comments.items.filter((row) => row.id !== comment.id);
        meetup.comments.count = data.count;
        m.redraw();
      })
      .catch(() => {
        delete this.removing[id];
        m.redraw();
      });
  }

  t(key: string) {
    return app.translator.trans('umbral-comunidad.forum.encuentros.' + key);
  }
}
